import { Injectable } from '@angular/core';
import { StateToken, State, StateContext, Selector, Action, Store } from '@ngxs/store';
import { patch } from '@ngxs/store/operators';
import { tap } from 'rxjs';
import { UserCrewManagementRoutes } from 'src/app/core/enums/routes.enum';
import { CrewManagementType } from 'src/app/department/enums/crew-management-types.enum';
import { TenantUserAccessState } from 'src/app/tenant-selector/state/tenant-user-access.state';
import { OnSuccess } from 'src/app/shared/decorators/on-success.decorator';
import { redirectAndBypassUnsavedDataCheck } from 'src/app/shared/functions/redirect-and-bypass-unsaved-data-check';
import { IdName } from 'src/app/shared/models/id-name';
import { ApiResponse } from 'src/app/core/models/api-response';
import {
  AddCrewMembersToTrip,
  AddPaxesToTrip,
  ClearCurrentTrip,
  CreateTrip,
  DeleteCrewMemberFromTrip,
  DeletePaxFromTrip,
  DeleteTrip,
  EndTrip,
  LoadAvailableTrips,
  LoadTripById,
  LoadTripLog,
  LoadTrips,
  LoadTripsChangeLog,
  LoadTripsForTenant,
  LoadTripsList,
  PrintTripPdf,
  UpdateCrewMember,
  UpdatePax,
  UpdateTripForActivityBased,
  UpdateTripForTripBased,
} from './trip.actions';
import {
  ITripResponse,
  ITripGroupData,
  IApiResponseGroupTrips,
  IAvailableTrip,
  ITripLog,
  ITripCreate,
  ITripChangeLog,
} from '../../models/trip.model';
import { TripApiService } from '../../services/trips-api.service';
import { ITripList } from '../../models/trip-list.model';

export interface TripStateModel {
  trips: ITripResponse[];
  tripsList: ITripList[];
  groupedTrips: ITripGroupData[];
  tripsForTenant: ITripResponse[];
  availableTrips: IAvailableTrip[];
  tripsChangeLog: ITripChangeLog[];
  tripsChangeLogTotalPages: number;
  tripsTotalPages: number;
  currentTrip: ITripResponse;
  currentTripPdf: Blob;
  tripLog: ITripLog[];
}

const TRIP_STATE_TOKEN = new StateToken<TripStateModel>('trips');

@State<TripStateModel>({
  name: TRIP_STATE_TOKEN,
  defaults: {
    trips: [],
    tripsList: [],
    tripsForTenant: [],
    groupedTrips: [],
    availableTrips: [],
    tripsChangeLog: [],
    tripsChangeLogTotalPages: 0,
    tripsTotalPages: 0,
    currentTrip: <ITripResponse>{},
    currentTripPdf: <Blob>{},
    tripLog: [],
  },
})
@Injectable()
export class TripState {
  constructor(
    private service: TripApiService,
    private store: Store
  ) {}

  @Action(LoadTrips, { cancelUncompleted: true })
  loadTrips(
    { getState, setState }: StateContext<TripStateModel>,
    { filter, departmentId }: LoadTrips
  ) {
    return this.service.getTrips(false, filter, departmentId).pipe(
      tap((response: IApiResponseGroupTrips) => {
        const state = getState();
        setState({
          ...state,
          groupedTrips: response.groupData,
          trips: response.data,
          tripsTotalPages: response.totalPages,
        });
      })
    );
  }

  @Action(LoadTripsList, { cancelUncompleted: true })
  loadTripsList(ctx: StateContext<TripStateModel>) {
    return this.service.getTripsList().pipe(
      tap((response: ITripList[]) => {
        ctx.setState(patch({ tripsList: response }));
      })
    );
  }

  @Action(LoadTripsForTenant, { cancelUncompleted: true })
  loadTripsForTenant(ctx: StateContext<TripStateModel>, { filter }: LoadTripsForTenant) {
    return this.service.getTrips(true, filter).pipe(
      tap((response: IApiResponseGroupTrips) => {
        ctx.setState(
          patch({ tripsForTenant: response.data, tripsTotalPages: response.totalPages })
        );
      })
    );
  }
  @Action(LoadAvailableTrips, { cancelUncompleted: true })
  loadAvailableTrips(ctx: StateContext<TripStateModel>) {
    return this.service.getAvailableTrips().pipe(
      tap((availableTrips: IAvailableTrip[]) => {
        ctx.setState(patch({ availableTrips }));
      })
    );
  }

  @Action(LoadTripById, { cancelUncompleted: true })
  loadTripById(
    { getState, setState }: StateContext<TripStateModel>,
    { id, departmentId }: LoadTripById
  ) {
    return this.service.getTripById(id, departmentId).pipe(
      tap((response: ITripResponse) => {
        const state = getState();
        setState({
          ...state,
          currentTrip: response,
        });
      })
    );
  }

  @Action(PrintTripPdf, { cancelUncompleted: true })
  printTripPdf(
    { getState, setState }: StateContext<TripStateModel>,
    { tripId, onlyCurrentlyOnBoard }: PrintTripPdf
  ) {
    return this.service.printTripPdf(tripId, onlyCurrentlyOnBoard).pipe(
      tap((file: Blob) => {
        const state = getState();
        setState({
          ...state,
          currentTripPdf: file,
        });
      })
    );
  }

  @Action(CreateTrip, { cancelUncompleted: true })
  @OnSuccess({ message: 'tripCreated' })
  createTrip(ctx: StateContext<TripStateModel>, { trip }: CreateTrip) {
    return this.service.createTrip(trip).pipe(
      tap((response: IdName) => {
        redirectAndBypassUnsavedDataCheck(
          `${UserCrewManagementRoutes.BasicTripDetails}/${response.id}`
        );
      })
    );
  }

  @Action(UpdateTripForTripBased, { cancelUncompleted: true })
  @OnSuccess({ url: UserCrewManagementRoutes.BasicTrips, message: 'tripUpdated' })
  updateTripForTripBased(ctx: StateContext<TripStateModel>, { trip }: UpdateTripForTripBased) {
    return this.service.updateTripForTripBased(trip);
  }

  @Action(UpdateTripForActivityBased, { cancelUncompleted: true })
  @OnSuccess({ url: UserCrewManagementRoutes.TripsDashboard, message: 'tripUpdated' })
  updateTripForActivityBased(
    ctx: StateContext<TripStateModel>,
    { trip }: UpdateTripForActivityBased
  ) {
    return this.service.updateTripForActivityBased(trip as ITripCreate);
  }

  @Action(EndTrip, { cancelUncompleted: true })
  @OnSuccess({ url: UserCrewManagementRoutes.TripsDashboard, message: 'tripEnded' })
  endTrip(ctx: StateContext<TripStateModel>, { tripId, endDate }: EndTrip) {
    return this.service.endTrip(tripId, endDate);
  }

  @Action(DeleteTrip, { cancelUncompleted: true })
  @OnSuccess({ message: 'tripDeleted' })
  deleteTrip(ctx: StateContext<TripStateModel>, { tripId }: DeleteTrip) {
    return this.service.deleteTrip(tripId).pipe(
      tap(() => {
        const currentDepartment = this.store.selectSnapshot(
          TenantUserAccessState.currentDepartment
        );
        const crewManagementType = currentDepartment?.crewManagementType;
        const redirectUrl =
          crewManagementType === CrewManagementType.TripBased
            ? UserCrewManagementRoutes.BasicTrips
            : UserCrewManagementRoutes.Main;
        redirectAndBypassUnsavedDataCheck(redirectUrl);
      })
    );
  }

  @Action(LoadTripLog, { cancelUncompleted: true })
  loadTripLog(ctx: StateContext<TripStateModel>, { tripId }: LoadTripLog) {
    return this.service.getTripLog(tripId).pipe(
      tap((log: ITripLog[]) => {
        ctx.setState(patch({ tripLog: log }));
      })
    );
  }

  @Action(AddCrewMembersToTrip, { cancelUncompleted: true })
  @OnSuccess({ message: 'crewMembersAddedToTrip' })
  addCrewMembersToTrip(
    ctx: StateContext<TripStateModel>,
    { tripId, userIds }: AddCrewMembersToTrip
  ) {
    return this.service.addCrewMembersToTrip(tripId, userIds).pipe(
      tap(() => {
        ctx.dispatch([new LoadTripById(tripId), new LoadTripLog(tripId)]);
      })
    );
  }

  @Action(UpdateCrewMember, { cancelUncompleted: true })
  @OnSuccess({ message: 'crewMemberUpdated' })
  updateCrewMember(
    ctx: StateContext<TripStateModel>,
    { tripId, crewMemberId, crewMember }: UpdateCrewMember
  ) {
    return this.service.updateCrewMember(tripId, crewMemberId, crewMember).pipe(
      tap(() => {
        ctx.dispatch([new LoadTripById(tripId), new LoadTripLog(tripId)]);
      })
    );
  }

  @Action(DeleteCrewMemberFromTrip, { cancelUncompleted: true })
  @OnSuccess({ message: 'crewMembersDeleted' })
  deleteCrewMemberFromTrip(
    ctx: StateContext<TripStateModel>,
    { tripId, crewMemberIds }: DeleteCrewMemberFromTrip
  ) {
    return this.service.deleteCrewMemberFromTrip(tripId, crewMemberIds).pipe(
      tap(() => {
        ctx.dispatch([new LoadTripById(tripId), new LoadTripLog(tripId)]);
      })
    );
  }

  @Action(AddPaxesToTrip, { cancelUncompleted: true })
  @OnSuccess({ message: 'paxesAddedToTrip' })
  addPaxesToTrip(ctx: StateContext<TripStateModel>, { tripId, paxesId }: AddPaxesToTrip) {
    return this.service.addPaxesToTrip(tripId, paxesId).pipe(
      tap(() => {
        ctx.dispatch([new LoadTripById(tripId), new LoadTripLog(tripId)]);
      })
    );
  }

  @Action(UpdatePax, { cancelUncompleted: true })
  @OnSuccess({ message: 'paxesUpdated' })
  updatePax(ctx: StateContext<TripStateModel>, { tripId, paxId, pax }: UpdatePax) {
    return this.service.updatePax(tripId, paxId, pax).pipe(
      tap(() => {
        ctx.dispatch([new LoadTripById(tripId), new LoadTripLog(tripId)]);
      })
    );
  }

  @Action(DeletePaxFromTrip, { cancelUncompleted: true })
  @OnSuccess({ message: 'paxesDeletedFromTrip' })
  deletePaxFromTrip(ctx: StateContext<TripStateModel>, { tripId, paxIds }: DeletePaxFromTrip) {
    return this.service.deletePaxFromTrip(tripId, paxIds).pipe(
      tap(() => {
        ctx.dispatch([new LoadTripById(tripId), new LoadTripLog(tripId)]);
      })
    );
  }

  @Action(LoadTripsChangeLog, { cancelUncompleted: true })
  loadTripsChangeLog(ctx: StateContext<TripStateModel>, { filter }: LoadTripsChangeLog) {
    return this.service.getTripsChangeLog(filter).pipe(
      tap((response: ApiResponse<ITripChangeLog[]>) => {
        ctx.setState(
          patch({ tripsChangeLog: response.data, tripsChangeLogTotalPages: response.totalPages })
        );
      })
    );
  }

  @Action(ClearCurrentTrip)
  clearCurrentTrip(ctx: StateContext<TripStateModel>) {
    ctx.setState(patch({ currentTrip: undefined }));
  }

  @Selector()
  static getTripsTotalPages(state: TripStateModel) {
    return state.tripsTotalPages;
  }

  @Selector()
  static getGroupedTrips(state: TripStateModel): ITripGroupData[] {
    return state.groupedTrips;
  }

  @Selector()
  static getTrips(state: TripStateModel): ITripResponse[] {
    return state.trips;
  }

  @Selector()
  static getTripsList(state: TripStateModel): ITripList[] {
    return state.tripsList;
  }

  @Selector()
  static getTripsForTenant(state: TripStateModel): ITripResponse[] {
    return state.tripsForTenant;
  }

  @Selector()
  static getAvailableTrips(state: TripStateModel): IAvailableTrip[] {
    return state.availableTrips;
  }

  @Selector()
  static getTripById(state: TripStateModel): ITripResponse {
    return state.currentTrip;
  }

  @Selector()
  static getTripAsPdf(state: any) {
    return state.currentTripPdf;
  }

  @Selector()
  static getTripLog(state: TripStateModel): ITripLog[] {
    return state.tripLog;
  }

  @Selector()
  static getTripsChangeLog(state: TripStateModel): ITripChangeLog[] {
    return state.tripsChangeLog;
  }

  @Selector()
  static getTripsChangeLogTotalPages(state: TripStateModel): number {
    return state.tripsChangeLogTotalPages;
  }
}
